// @flow
import {
  Box,
  Button,
  Grid,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Typography,
} from '@material-ui/core';
import { motion } from 'framer-motion';
import { InView } from 'react-intersection-observer';
import { GatsbyImage } from 'gatsby-plugin-image';
import { getShopifyImage } from 'gatsby-source-shopify';
import { graphql, Link, navigate } from 'gatsby';
import { makeStyles } from '@material-ui/core/styles';
import * as React from 'react';

import { formatPrice } from '../utils/format-price';
import WhiteSpace from './styled/white-space';
import pointer from '../images/pointer.png';

type ProductCardProps = {
  product: any,
  eager: any,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  tag: {
    backgroundColor: theme.palette.error.main,
    color: 'white',
    margin: 0,
    padding: 5,
    width: 120,
    textAlign: 'center',
    textTransform: 'uppercase',
    fontWeight: 'bold',

    '& .MuiTypography-body1': {
      fontWeight: 'bold',
    },
  },
  listItemBar: {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
  },
  link: {
    cursor: `url(${pointer}), auto`,
  },
  empty: {
    backgroundColor: 'transparent',
    color: 'transparent',
    margin: 0,
    padding: 5,
    width: 120,
    textAlign: 'center',
    textTransform: 'uppercase',
    fontWeight: 'bold',
  },
  title: {
    textTransform: 'uppercase',
  },
}));

export function ProductCard({ product, eager }: ProductCardProps) {
  const {
    title,
    priceRangeV2,
    slug,
    images: [firstImage],
    // vendor,
    storefrontImages,
  } = product;
  const classes = useStyles();

  const price = formatPrice(
    priceRangeV2.minVariantPrice.currencyCode,
    priceRangeV2.minVariantPrice.amount,
  );

  const maxPrice = formatPrice(
    priceRangeV2.maxVariantPrice.currencyCode,
    priceRangeV2.maxVariantPrice.amount,
  );

  const defaultImageHeight = 200;
  const defaultImageWidth = 200;
  let storefrontImageData = {};

  if (storefrontImages) {
    const storefrontImage = storefrontImages.edges[0].node;

    try {
      storefrontImageData = getShopifyImage({
        image: storefrontImage,
        layout: 'fixed',
        width: defaultImageWidth,
        height: defaultImageHeight,
      });
    } catch (e) {
      /* eslint-disable-next-line */
      console.error(e);
    }
  }

  const hasImage =
    firstImage || Object.getOwnPropertyNames(storefrontImageData || {}).length;

  const variants = {
    visible: { opacity: 1 },
    hidden: {
      opacity: 0,
    },
  };

  return (
    <InView threshold={0.5}>
      {({ inView, ref }) => (
        <Grid
          item
          xs={12}
          lg={3}
          md={4}
          sm={6}
          component={motion.div}
          animate={inView ? 'visible' : 'hidden'}
          ref={ref}
          variants={variants}
          transition={{ duration: 0.7, ease: 'easeOut' }}
        >
          <Link
            className={classes.link}
            to={slug}
            aria-label={`Ver la página de ${title} `}
          >
            <Box display="flex" justifyContent="center">
              <div className={maxPrice !== price ? classes.tag : classes.empty}>
                <Typography>Descuento</Typography>
              </div>
            </Box>
            <ImageList className={classes.root} cols={1} rowHeight={270}>
              <ImageListItem cols={1}>
                {hasImage ? (
                  <GatsbyImage
                    alt={firstImage?.altText ?? title}
                    image={firstImage?.gatsbyImageData ?? storefrontImageData}
                    loading={eager ? 'eager' : 'lazy'}
                  />
                ) : (
                  <div
                    style={{
                      height: defaultImageHeight,
                      width: defaultImageWidth,
                    }}
                  />
                )}
                <ImageListItemBar
                  className={classes.listItemBar}
                  title={
                    <Typography
                      className={classes.title}
                      align="center"
                      variant="h6"
                    >
                      {title}
                    </Typography>
                  }
                  subtitle={
                    <Typography align="center" variant="subtitle1">
                      {price}
                    </Typography>
                  }
                />
              </ImageListItem>
            </ImageList>
          </Link>

          <WhiteSpace size="1rem" />

          <Box display="flex" justifyContent="center">
            <Button
              Size="large"
              variant="contained"
              type="button"
              color="primary"
              onClick={() => navigate(slug)}
            >
              Comprar
            </Button>
          </Box>
        </Grid>
      )}
    </InView>
  );
}

export const query = graphql`
  fragment ProductCard on ShopifyProduct {
    id
    title
    slug: gatsbyPath(
      filePath: "/products/{ShopifyProduct.productType}/{ShopifyProduct.handle}"
    )
    images {
      id
      altText
      gatsbyImageData(aspectRatio: 1, width: 640)
    }
    priceRangeV2 {
      maxVariantPrice {
        currencyCode
        amount
      }
      minVariantPrice {
        amount
        currencyCode
      }
    }
    vendor
  }
`;
